import { Theme, useTheme } from '@app/providers/theme-provider'
import { ConfigProvider as AntdConfigProvider, theme as antdTheme } from 'antd'
import ruRU from 'antd/locale/ru_RU'
import { useMemo } from 'react'
import { colors } from '../lib/theme'

interface ConfigProviderProps {
    children: JSX.Element
}

export function ConfigProvider({ children }: ConfigProviderProps) {
    const { theme } = useTheme()
    const isLightTheme = useMemo(() => theme === Theme.LIGHT, [theme])

    const bgLayoutColor = isLightTheme ? colors.lightBgLayoutColor : colors.darkBgLayoutColor
    const bgContainerColor = isLightTheme ? colors.lightBgContainerColor : colors.darkBgContainerColor
    const textColor = isLightTheme ? colors.lightTextColor : colors.darkTextColor
    const headerColor = isLightTheme ? colors.lightHeaderColor : colors.darkHeaderColor
    const siderColor = isLightTheme ? colors.lightSiderColor : colors.darkSiderColor
    const triggerBgColor = isLightTheme ? colors.lightTriggerBgColor : colors.darkTriggerBgColor
    const menuBgColor = isLightTheme ? colors.lightMenuBgColor : colors.darkMenuBgColor
    const elevatedBgColor = isLightTheme ? colors.lightElevatedBgColor : colors.darkElevatedBgColor
    const elevatedMenuBgColor = isLightTheme ? colors.lightSiderColor : colors.darkElevatedBgColor
    const highlightColor = isLightTheme ? colors.lightColorHighlight : colors.darkColorHighlight
    const menuItemColor = isLightTheme ? colors.lightMenuItemColor : colors.darkMenuItemColor
    const menuItemHoverColor = isLightTheme ? colors.lightMenuItemHoverColor : colors.darkMenuItemHoverColor
    const menuItemSelectedColor = isLightTheme ? colors.lightMenuItemSelectedColor : colors.darkMenuItemSelectedColor
    const menuItemBgSelectedColor = isLightTheme
        ? colors.lightMenuItemSelectedBgColor
        : colors.darkMenuItemSelectedBgColor
    const submentItemBgColor = isLightTheme ? colors.lightSubmenuItemBgColor : colors.darkSubmenuItemBgColor
    const tableHeaderBgColor = isLightTheme ? colors.lightTableHeaderBgColor : colors.darkTableHeaderBgColor
    const tableBorderColor = isLightTheme ? colors.lightTableBorderColor : colors.darkTableBorderColor
    const colorSplit = isLightTheme ? colors.lightColorSplit : colors.darkColorSplit
    const colorBorderSecondary = isLightTheme ? colors.lightColorBorderSecondary : colors.darkColorBorderSecondary
    const textLabelColor = isLightTheme ? colors.lightTextLabelColor : colors.darkTextLabelColor

    return (
        <AntdConfigProvider
            locale={ruRU}
            theme={{
                cssVar: true,
                algorithm: isLightTheme ? antdTheme.defaultAlgorithm : antdTheme.darkAlgorithm,
                token: {
                    borderRadius: 8,
                    colorPrimary: colors.primaryColor,
                    colorLink: colors.primaryColor,
                    colorBgLayout: bgLayoutColor,
                    colorBgContainer: bgContainerColor,
                    colorText: textColor,

                    colorBgElevated: elevatedBgColor,
                    colorHighlight: highlightColor,
                    colorSplit: colorSplit,
                    colorBorderSecondary: colorBorderSecondary,
                    colorTextLabel: textLabelColor,
                    fontFamily: 'SF Pro Display'
                },
                components: {
                    Layout: {
                        headerPadding: '0 16px',
                        triggerBg: triggerBgColor,
                        triggerColor: bgLayoutColor,
                        headerBg: headerColor,
                        siderBg: siderColor
                    },
                    Menu: {
                        activeBarBorderWidth: 0,
                        itemBg: menuBgColor,
                        itemColor: menuItemColor,
                        itemHoverColor: menuItemHoverColor,
                        itemSelectedBg: menuItemBgSelectedColor,
                        itemSelectedColor: menuItemSelectedColor,
                        colorBgElevated: elevatedMenuBgColor,
                        popupBg: menuBgColor,
                        subMenuItemBg: submentItemBgColor
                    },
                    Modal: {
                        titleFontSize: 20
                    },
                    Table: {
                        headerBg: tableHeaderBgColor,
                        borderColor: tableBorderColor
                    },
                    Form: {
                        labelColor: textLabelColor
                    },
                    Rate: {
                        starSize: 32
                    },
                    Select: {
                        selectorBg: bgLayoutColor
                    }
                }
            }}
        >
            {children}
        </AntdConfigProvider>
    )
}
